import React, { useState, useEffect } from "react";
import axios from "axios";
import { baseURL } from "../../config";
import setUpAxios from "../../setUpAxios";
import { getCachedUsers, setCachedUsers } from "../../utils/userCache.jsx";

const UserList = ({ onSelectUser, selectedUser, onUserChange, onDataLoaded }) => {
  const [users, setUsers] = useState([]); 
  const [selectedUserId, setSelectedUserId] = useState(""); 
  const [visible, setVisible] = useState(false); 

  // Fetch users on component mount
  useEffect(() => {
    const cached = getCachedUsers();
    if (cached) {
      setUsers(cached);
      // IMPORTANT: Notify parent even if data came from cache
      if (onDataLoaded) {
        onDataLoaded(cached);
      }
    } else {
      setUpAxios();
      axios
        .get(`${baseURL}/users`)
        .then((response) => {
          if (Array.isArray(response.data)) {
            setUsers(response.data);
            setCachedUsers(response.data); 
            // Notify parent when data arrives from API
            if (onDataLoaded) {
              onDataLoaded(response.data);
            }
          } else {
            console.error("Unexpected response format:", response.data);
          }
        })
        .catch((error) => {
          console.error("Error fetching users:", error);
        });
    }
  }, []); // Remove onDataLoaded from dependency array to avoid loops

  // When 'selectedUser' prop or 'users' array changes, try to select the correct user.
  useEffect(() => {
    if (selectedUser && users.length > 0) {
        const foundUser = users.find(
          (user) => user.username === selectedUser
        );
        if (foundUser) {
          setSelectedUserId(foundUser.id.toString());
          onSelectUser(foundUser.id.toString()); 
          if(onUserChange){
            onUserChange(foundUser.name.toString());
          }
        }
    }
  }, [selectedUser, users]);

  const handleUserSelect = (event) => {
    const userId = event.target.value;
    setSelectedUserId(userId);
    onSelectUser(userId); 
    if(onUserChange){
      onUserChange(event.target.name);
    }
    setVisible(false);
  };

  const selectedUserObj = users.find(
    (user) => user.id === parseInt(selectedUserId)
  );

  return (
    <div className="relative w-full max-w-2xl p-2 bg-white border border-gray-300 rounded-lg shadow-md">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setVisible(!visible)}>
        <div className="flex items-center space-x-4">
          {selectedUserId && selectedUserObj ? (
            <>
              {selectedUserObj.profile_img ? (
                <img
                  className="h-10 w-10 rounded-full object-cover"
                  src={selectedUserObj.profile_img}
                  alt="Profile"
                />
              ) : (
                <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center text-white font-semibold">
                  {selectedUserObj.name?.[0]?.toUpperCase()}
                </div>
              )}

              <div className="flex flex-col">
                <p className="font-semibold text-gray-800">{selectedUserObj.name}</p>
                <p className="text-sm text-gray-500">{selectedUserObj.username}</p>
              </div>
            </>
          ) : (
            <p className="text-gray-500 font-medium">Select a customer</p>
          )}
        </div>
        <span className="text-gray-600 text-xl">
          {visible ? "▲" : "▼"}
        </span>
      </div>

      {visible && (
        <div className="absolute left-0 top-full mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-[120]">
          <ul className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {users.map((user) => (
              <li
                key={user.id}
                onClick={() => handleUserSelect({ target: { value: user.id, name: user.name } })}
                className="flex items-center px-4 py-3 hover:bg-yellow-100 transition-all cursor-pointer"
              >
                {user.profile_img ? (
                  <img
                    className="h-10 w-10 rounded-full object-cover"
                    src={user.profile_img}
                    alt="Profile"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center text-white font-semibold">
                    {user.name?.[0]?.toUpperCase()}
                  </div>
                )}

                <div className="ml-3">
                  <p className="font-medium text-gray-800">{user.name}</p>
                  <p className="text-sm text-gray-500">{user.username}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default UserList;